import {ChangeDetectionStrategy, Component, inject, InjectionToken} from '@angular/core';
import {AsyncPipe, NgTemplateOutlet} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    map,
    Observable,
    of,
    startWith,
    switchMap,
} from 'rxjs';
import {
    FeatureFlagService,
    fromControlWithDebounceTime,
    TranslatePipe,
} from '@brunata-angular/util';
import {LoadComponent} from '@brunata-angular/ui';
import {systemInformationLink} from '@shared/route-utils';
import {MAX_SEARCH_QUERY_LENGTH, MIN_SEARCH_QUERY_LENGTH, SearchService} from '../search.service';
import {
    SearchConsumer,
    SearchLocation,
    SearchMeter,
    SearchResults,
    SearchSystem,
} from '../search.model';

export type SearchModalInput = {
    query: string;
};

type SearchRow<TItem> = {
    item: TItem;
    link: string[];
};

type SearchSystemView = SearchSystem & {
    proBraSummary: string;
};

type SearchGroupView<TItem> = {
    totalCount: number;
    rows: SearchRow<TItem>[];
};

type SearchView =
    | {status: 'tooShort'}
    | {status: 'loading'}
    | {status: 'error'}
    | {
          status: 'loaded';
          hasHits: boolean;
          hasPlaceHits: boolean;
          systems: SearchGroupView<SearchSystemView>;
          locations: SearchGroupView<SearchLocation>;
          consumers: SearchGroupView<SearchConsumer>;
          meters: SearchGroupView<SearchMeter>;
      };

// The consumers page only routes while kehe-online-project is on; the legacy residents page covers it otherwise.
const residentsSegment = (newResidentsPage: boolean): string =>
    newResidentsPage ? 'consumers' : 'residents';

const systemConsumersLink = (buildingNo: string, newResidentsPage: boolean): string[] => [
    '/admin',
    'system',
    buildingNo,
    residentsSegment(newResidentsPage),
];

const consumerOverviewLink = (
    buildingNo: string,
    locationId: string,
    consumerId: string,
    newResidentsPage: boolean,
): string[] => [
    '/admin',
    'system',
    buildingNo,
    residentsSegment(newResidentsPage),
    locationId,
    'resident',
    consumerId,
];

const meterOverviewLink = (buildingNo: string, meterId: string): string[] => [
    '/admin',
    'system',
    buildingNo,
    'meters',
    'meters-and-sensors',
    meterId,
];

@Component({
    selector: 'app-search-modal',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        NgTemplateOutlet,
        ReactiveFormsModule,
        RouterLink,
        TranslatePipe,
        LoadComponent,
    ],
    templateUrl: './search-modal.component.html',
    styleUrl: './search-modal.component.scss',
})
export class SearchModalComponent {
    static readonly inputToken = new InjectionToken<SearchModalInput>('SearchModalComponent.input');

    private readonly input = inject(SearchModalComponent.inputToken);
    private readonly searchService = inject(SearchService);
    private readonly featureFlagService = inject(FeatureFlagService);
    protected readonly activeModal = inject(NgbActiveModal);

    protected readonly minQueryLength = MIN_SEARCH_QUERY_LENGTH;
    protected readonly maxQueryLength = MAX_SEARCH_QUERY_LENGTH;
    protected readonly searchControl = new FormControl(this.input.query, {nonNullable: true});

    protected readonly view$ = fromControlWithDebounceTime(this.searchControl).pipe(
        map(query => query.trim()),
        distinctUntilChanged(),
        switchMap((query): Observable<SearchView> => {
            if (query.length < MIN_SEARCH_QUERY_LENGTH) {
                return of({status: 'tooShort'} as const);
            }
            return this.search(query);
        }),
    );

    protected clear(): void {
        this.searchControl.setValue('');
    }

    private search(query: string): Observable<SearchView> {
        return combineLatest([
            this.searchService.search(query),
            this.featureFlagService.isFeatureFlagEnabled$('kehe-online-project'),
        ]).pipe(
            map(([results, newResidentsPage]): SearchView => toView(results, newResidentsPage)),
            startWith({status: 'loading'} as const),
            catchError(() => of({status: 'error'} as const)),
        );
    }
}

function toGroupView<TItem>(
    group: {totalCount: number; items: TItem[]},
    linkFor: (item: TItem) => string[],
): SearchGroupView<TItem> {
    return {
        totalCount: group.totalCount,
        rows: group.items.map(item => ({item, link: linkFor(item)})),
    };
}

const MAX_PRO_BRA_PAIRS_SHOWN = 5;

function toSystemView(system: SearchSystem): SearchSystemView {
    const shownPairs = system.buildingIdPairs.slice(0, MAX_PRO_BRA_PAIRS_SHOWN).join(', ');
    return {
        ...system,
        proBraSummary:
            system.buildingIdPairs.length > MAX_PRO_BRA_PAIRS_SHOWN
                ? `${shownPairs}, …`
                : shownPairs,
    };
}

function toView(results: SearchResults, newResidentsPage: boolean): SearchView {
    const systems = toGroupView(
        {totalCount: results.systems.totalCount, items: results.systems.items.map(toSystemView)},
        item => systemInformationLink(item.buildingNo),
    );
    const locations = toGroupView(results.locations, item =>
        item.consumerId == null
            ? systemConsumersLink(item.buildingNo, newResidentsPage)
            : consumerOverviewLink(
                  item.buildingNo,
                  item.locationId,
                  item.consumerId,
                  newResidentsPage,
              ),
    );
    const consumers = toGroupView(results.consumers, item =>
        consumerOverviewLink(item.buildingNo, item.locationId, item.consumerId, newResidentsPage),
    );
    const meters = toGroupView(results.meters, item =>
        meterOverviewLink(item.buildingNo, item.meterId),
    );
    const hasPlaceHits = systems.totalCount + locations.totalCount + meters.totalCount > 0;
    return {
        status: 'loaded',
        hasHits: hasPlaceHits || consumers.totalCount > 0,
        hasPlaceHits,
        systems,
        locations,
        consumers,
        meters,
    };
}
